<template>
    <div>
        <template v-for="( key, index ) in headers">
            <span
                :class="[
                    'table__body__row__text',
                    `table__${model}__col__${index}`
                ]"
                :key="index"
            >
                <template v-if="key === 'name'">
                    <component
                        v-if="row['group'].id"
                        :class="[
                            'row__call-rules__group-component',
                            getGroupComponentSetting({ groupId: row['group'].id, property: 'class' })
                        ]"
                        :is="getGroupComponentSetting({ groupId: row['group'].id })"
                    />
                    {{ row['description'] ? row['description'] : '---' }}
                </template>
                <template v-else-if="key === 'limitNumberOfAttempts' && hasResolution">
                    {{ row['resolution'].retryLimit || '---' }}
                </template>
                <template v-else-if="key === 'intervalOfAttempts' && hasResolution">
                    {{ getIntervalOfAttempts }}
                </template>
                <template v-else-if="key === 'price'">
                    {{ row['billable'] }}
                </template>
                <template v-else-if="key === 'notify'">
                    {{ hasEmailAlert() }}
                </template>
                <template v-else-if="key === 'sharingRules'">
                    {{ hasSharingRules() }}
                </template>
                <template v-else>
                    {{ row[key] || '---' }}
                </template>
            </span>
        </template>
        <div
            :class="[
                'row__call-rules__actions display--flex',
                `table__${model}__col__6`,
                { 'row__call-rules__actions--end': row.enabled || !$store.getters.can( 'campaign', 'edit' ) }
            ]"
        >
            <table-actions
                v-if="!row.enabled"
                :settings="{
                    model: 'script',
                    delete: {
                        action: deleteContactCallState
                    }
                }"
            />
        </div>
    </div>
</template>

<script>
import { computed } from '@vue/composition-api'; // composition API
import { useActions } from '@/hooks/use-store'; // store
import i18n from '@/plugins/i18n'; // i18n
import tableStructure from '@/hooks/use-table'; // hooks

export default {
    components: {
        TableActions: () => import( /* webpackChunkName: 'TableActions' */ '@/components/bundles/tables/packs/TableActions' ),
        IconPositive: () => import( /* webpackChunkName: 'IconPositive' */ '@/components/bundles/layout/images/icons/IconPositive' ),
        IconReschedule: () => import( /* webpackChunkName: 'IconReschedule' */ '@/components/bundles/layout/images/icons/IconReschedule' ),
        IconNegative: () => import( /* webpackChunkName: 'IconNegative' */ '@/components/bundles/layout/images/icons/IconNegative' )
    },
    ...tableStructure,
    setup ( props, { parent }) {
        // obtenção das acções da store.
        const { actions } = useActions();

        // verifica se o estado tem informações de resolução.
        const hasResolution = computed( () => props?.row?.resolution !== null );

        // Obtém os intervalos das tentativas.
        const getIntervalOfAttempts = computed( () => props?.row?.resolution?.retryIn?.join( ', ' ) || '---' );

        // Transmite se existe partilha de regras.
        const hasSharingRules = () => {
            return ( props?.row?.resolution?.sharedRetryLimit === true ) ? i18n.t( 'global.yes' ) : i18n.t( 'global.no' );
        };

        // Transmite se há alertas de e-mail para este estado.
        const hasEmailAlert = () => {
            return ( props?.row?.notifiable === true ) ? i18n.t( 'global.yes' ) : i18n.t( 'global.no' );
        };

        /**
         * [method]
         * Emissão das configurações da página, como
         * o bloqueio da barra de acções e abertura
         * da modal de confirmação.
         *
         * @param {String} type
         * @since 1.2.8
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const emitConfigs = ( type = 'active' ) => {
            // bloqueia a barra de adição & edição.
            actions( 'controlActionBar', { value: true });

            // trigger para abrir a modal.
            parent.$emit( 'open-modal', { actionType: type });
        };

        /**
         * [method]
         * Apagar do estado da chamada.
         * É emitido as configs & definição dos
         * valores a serem enviados para API.
         *
         * @since 1.2.8
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const deleteContactCallState = () => {
            emitConfigs( 'delete' );

            // definição do ID do estado e tipo de operação.
            actions( 'registerField', { name: 'contactCallStateId', value: props.row.id });
            actions( 'registerField', { name: 'contactCallStateOperation', value: 'delete' });
        };

        /**
         * [method]
         * Ao receber o id do grupo do estado da chamada, devolve
         * o nome do componente relacionado com esse grupo.
         *
         * @param {Number} groupId
         * @return {String} // Nome do componente ou class
         * @since 1.2.8
         * @category Method
         * @autor Jorge Monteiro
         *
        */
        const getGroupComponentSetting = ({ groupId, property = 'name' }) => {
            const groupComponentNames = {
                2: { name: 'IconPositive', class: 'row__call-rules__group-component--green' },
                3: { name: 'IconNegative', class: 'row__call-rules__group-component--red' },
                4: { name: 'IconReschedule', class: '' }
            };

            return groupComponentNames[groupId]?.[property] || '';
        };

        return {
            hasResolution,
            getIntervalOfAttempts,
            hasSharingRules,
            hasEmailAlert,
            deleteContactCallState,
            getGroupComponentSetting
        };
    }
};
</script>

<style lang="scss">
    .row {
        &__call-rules {
            &__actions {
                gap: 10px;
                &--end {
                    justify-content: flex-end;
                }
            }
            &__group-component {
                @include dimensions( 20px, auto );
                margin-right: 10px;
                &--green {
                    fill: $color-green;
                }
                &--red {
                    fill: $color-red;
                }
            }
        }
    }
</style>